import type { AdminTab } from "@/App"
import type { AppUser, CurrentUser, Destination } from "@/lib/api"
import { AppUsersPage } from "@/pages/AppUsersPage"
import { CertificatesPage } from "@/pages/CertificatesPage"
import { DestinationsPage } from "@/pages/DestinationsPage"
import { MailSettingsPage } from "@/pages/MailSettingsPage"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { KeyRound, Mail, Server, Users } from "lucide-react"

type Props = {
  currentUser: CurrentUser
  destinations: Destination[]
  activeTab: AdminTab
  onTabChange: (tab: AdminTab) => void
  onDestinationsChange: (destinations: Destination[]) => void
  onMachineChange: (machineName: string) => void
  onUsersRefresh: (users: AppUser[]) => void
}

export function AdminPage({
  currentUser,
  destinations,
  activeTab,
  onTabChange,
  onDestinationsChange,
  onMachineChange,
  onUsersRefresh,
}: Props) {
  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => onTabChange(value as AdminTab)}
      className="flex min-h-0 min-w-0 flex-1 flex-col gap-0 overflow-hidden"
    >
      <div className="shrink-0 border-b px-4 pt-4 sm:px-6 md:px-8">
        <div className="mb-4">
          <h1 className="text-lg font-semibold tracking-tight">Admin</h1>
          <p className="text-sm text-muted-foreground">
            Configurações administrativas do painel.
          </p>
        </div>
        <TabsList className="mb-[-1px] max-w-full overflow-x-auto" variant="line">
          <TabsTrigger value="destinations">
            <Server />
            Ambientes
          </TabsTrigger>
          <TabsTrigger value="certificates">
            <KeyRound />
            Certificados
          </TabsTrigger>
          <TabsTrigger value="app-users">
            <Users />
            Usuários do App
          </TabsTrigger>
          <TabsTrigger value="mail-settings">
            <Mail />
            Configuração de e-mail
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent
        value="destinations"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <DestinationsPage
          currentUser={currentUser}
          destinations={destinations}
          onDestinationsChange={onDestinationsChange}
          onMachineChange={onMachineChange}
        />
      </TabsContent>
      <TabsContent
        value="certificates"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <CertificatesPage currentUser={currentUser} />
      </TabsContent>
      <TabsContent
        value="app-users"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <AppUsersPage
          currentUser={currentUser}
          onUsersRefresh={onUsersRefresh}
        />
      </TabsContent>
      <TabsContent
        value="mail-settings"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <MailSettingsPage currentUser={currentUser} />
      </TabsContent>
    </Tabs>
  )
}
